// Activity heatmap — shows daily scored activity for the last year.
// Reads `activity_events` (standups, ticket updates, note/KB edits, meet
// calls). Intensity keyed on SUM(score) per day. Standup days keep a ✦
// marker in the tooltip so "did standup today" stays explicit even when
// bundled with other activity.

// getSDK comes from @symbo-ls/workspace-shared, stubbed by frank's
// EXTERNAL_PACKAGES at serialization time — reach it via root.call instead
// of a raw import (see TicketDetailModal.js for the full rationale).

// WS-SWITCH-INCOMPLETE-1 — both fetches below write AFTER an await, so a reply
// issued in workspace A can land in workspace B. Package-local module, not the
// shell's: a sub-app must not import the shell (see the module's own note).
import { sameWorkspaceWriter, wsIdOf } from '../functions/sameWorkspaceWriter.js'

// ⛔ THE OTHER HALF OF THE DRAIN: WITHOUT THIS, WIPING IS A BLANK SURFACE.
// `_activityData` is now workspace-scoped, so the switch empties it. The only
// fetch used to be `onCreate`, and the shell does NOT tear this widget down on
// a tenant boundary — it is bare-keyed on the HOME dashboard (workspace
// pages/main.js) and inside MyViewMain, both of which live through a switch.
// So an empty year-grid would have stayed empty until the user navigated away
// and back.
//
// The stamp is the TENANT, not a latch: it lives on the ELEMENT and is compared
// against the live workspace, so it cannot strand the key the way a module-level
// "already fetched" memo would (that is the class this ticket's second residue
// plane names). Switching A -> B -> A refetches all three times, correctly.
// Returns true exactly once per (element, workspace) pair.
export const _activityRefetchNeeded = (el, root) => {
  const wsId = wsIdOf(root)
  // `workspace` is absent for the first beats of boot. Stamping '' here would
  // make the first REAL workspace look like a change away from a fetched
  // state rather than the first fetch, and onCreate would be skipped.
  if (!wsId || !el) return false
  if (el._activityWsId === wsId) return false
  el._activityWsId = wsId
  return true
}

const WEEKS = 52
const DAYS = 7
const TOTAL = WEEKS * DAYS

// Scored buckets: intensity ramps on summed points, not raw row count,
// so a single meet call (4pt) outshines two note edits (2pt).
const _intensity = (score, hasStandup) => {
  // Empty days read as a slight gray fill (2026-06-06 per Nika) —
  // the previous transparent + 1px border rectangles looked like a
  // wireframe grid. Dialed down to .03 ("more slight").
  if (!score && !hasStandup) return 'white.03'
  if (hasStandup && !score) return 'green.15'
  if (score <= 2) return 'green.15'
  if (score <= 5) return 'green.3'
  if (score <= 10) return 'green.5'
  return 'green.7'
}

export const TicketsActivityHeatmap = {
  flow: 'y',
  gap: 'Z',

  CaptionTitle: {
    fontSize: 'Y2',
    Text: { text: '{{ tickets.heatmap.title | polyglot }}' }
  },

  Grid: {
    display: 'grid',
    gridTemplateColumns: `repeat(${WEEKS}, 1fr)`,
    gridTemplateRows: `repeat(${DAYS}, 1fr)`,
    gridAutoFlow: 'column',
    gap: 'X2',
    padding: 'Z 0',

    childExtends: 'TicketsHeatmapCell',
    childrenAs: 'state',
    children: (el, s) => {
      // Past first paint only: the full year is 364 cells (~0.7 s of a cold
      // home mount, dev deploy 31). Until the shell's stage ladder has started
      // (root._shellStage >= 1 - one macrotask after the mount, see the
      // shell's functions/shellStages.js) the grid holds ONE column of hidden
      // cells: same rows, same cell size, so the card keeps its height and
      // nothing moves when the year fills in. Outside the shell (no ladder,
      // no _shellStage) the year renders at once. One-way: never back.
      const stage = s.root?._shellStage
      if (!el.__heatmapGridReady) {
        if (stage !== undefined && !(stage >= 1)) {
          return Array.from({ length: DAYS }, () => ({
            date: '',
            score: 0,
            standups: 0,
            breakdown: {},
            placeholder: true
          }))
        }
        el.__heatmapGridReady = true
      }
      const data = s.root?._activityData || {}
      const today = new Date()
      const cells = []
      for (let i = TOTAL - 1; i >= 0; i--) {
        const d = new Date(today)
        d.setDate(d.getDate() - i)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
          d.getDate()
        ).padStart(2, '0')}`
        const entry = data[key] || {}
        cells.push({
          date: key,
          score: entry.score || 0,
          standups: entry.standups || 0,
          breakdown: entry.breakdown || {}
        })
      }
      return cells
    }
  },

  Legend: {
    tag: 'div',
    flow: 'x',
    gap: 'Z',
    align: 'center right',
    fontSize: 'Y1',
    color: 'caption',

    Less: { tag: 'span', text: '{{ tickets.heatmap.legendLess | polyglot }}' },
    Box_1: { width: 'Y', height: 'Y', round: 'X', background: 'white.03' },
    Box_2: { width: 'Y', height: 'Y', round: 'X', background: 'green.15' },
    Box_3: { width: 'Y', height: 'Y', round: 'X', background: 'green.3' },
    Box_4: { width: 'Y', height: 'Y', round: 'X', background: 'green.5' },
    Box_5: { width: 'Y', height: 'Y', round: 'X', background: 'green.7' },
    More: { tag: 'span', text: '{{ tickets.heatmap.legendMore | polyglot }}' }
  },

  DetailsCard: {
    hide: (el, s) => !s.root?._activitySelectedDate,
    flow: 'y',
    gap: 'Z1',
    marginTop: 'Z',
    padding: 'A',
    round: 'radiusCard',
    // Outline removed — banned (design-direction.md §8); `background:
    // 'title.04'` already gives this popover a tonal wash a step off the
    // heatmap's own ground, same precedent as ActivityChart's
    // line-bordered → overlay-fill conversion.
    background: 'title.04',

    Header: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',

      Title: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const date = s.root?._activitySelectedDate
          if (!date) return ''
          const d = new Date(date + 'T00:00:00')
          return el.call('localizedDate', d, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
        }
      },

      CloseBtn: {
        extends: 'CloseButton',
        onClick: (e, el, s) =>
          s.root?.update({ _activitySelectedDate: null, _activityDetails: null })
      }
    },

    Loading: {
      hide: (el, s) => !s.root?._activitySelectedDate || !!s.root?._activityDetails,
      flow: 'y',
      gap: 'V',
      Row_1: { extends: 'SkeletonRow', width: '70%' },
      Row_2: { extends: 'SkeletonRow', width: '45%' }
    },

    Empty: {
      hide: (el, s) => {
        const list = s.root?._activityDetails
        return !Array.isArray(list) || list.length > 0
      },
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      text: '{{ tickets.heatmap.empty | polyglot }}'
    },

    List: {
      hide: (el, s) => !(s.root?._activityDetails || []).length,
      flow: 'y',
      gap: 'V',
      childExtends: 'TicketsActivityRow',
      childrenAs: 'state',
      children: (el, s) => s.root?._activityDetails || []
    }
  },

  // Fetch real activity data — ticket updates + standup completions.
  // Re-armed on every workspace change: see _activityRefetchNeeded above.
  onCreate: (el, s) => _ticketsLoadActivity(el, s.root),

  // WS-SWITCH-INCOMPLETE-1: the widget outlives the switch, so the refetch has
  // to be driven from here. `subscribeTo` is required — the factory alone
  // registers no dependency on `workspace`, and this component's own reads are
  // all of `_activityData`.
  subscribeTo: ['workspace'],
  onUpdate: (el, s) => _ticketsLoadActivity(el, s.root)
}

const _ticketsLoadActivity = async (el, root) => {
  if (!root) return
  if (!_activityRefetchNeeded(el, root)) return
  // ⛔ CAPTURED HERE, BEFORE THE EMAIL POLL AND THE RPC. The reply must be
  // dropped if the user moved on; if it is dropped the tenant stamp above is
  // left pointing at the OLD workspace, so the next onUpdate re-arms and the
  // new tenant's grid still loads. Dropping is self-correcting, not a loss.
  const _write = sameWorkspaceWriter(root)
  let email = root.currentUserEmail
  if (!email) {
    await new Promise((resolve) => {
      let attempts = 0
      const check = setInterval(() => {
        email = root.currentUserEmail
        if (email || ++attempts > 30) {
          clearInterval(check)
          resolve()
        }
      }, 200)
    })
  }
  if (!email) return

  try {
    // migrated 2026-04-27 to SDK route workspaceProject.activity heatmap
    const since = new Date()
    since.setDate(since.getDate() - 365)
    const sinceDate = since.toISOString().slice(0, 10)

    // Single RPC — wrapper aggregates every activity type and scopes
    // by caller's user_email + workspace_id from the JWT.
    const result = await el.call('getSDK').execute('workspaceProject.activity', 'heatmap', {
      filter: {
        user_email: email,
        since: sinceDate,
        select: 'activity_type, score, occurred_on'
      }
    })
    const data = Array.isArray(result?.data) ? result.data : result?.data || result || []

    const counts = {}
    for (const row of data || []) {
      const day = row.occurred_on
      if (!day) continue
      if (!counts[day]) counts[day] = { score: 0, standups: 0, breakdown: {} }
      counts[day].score += row.score || 0
      counts[day].breakdown[row.activity_type] = (counts[day].breakdown[row.activity_type] || 0) + 1
      if (row.activity_type === 'standup') counts[day].standups++
    }

    _write({ _activityData: counts })
  } catch (err) {
    console.error('[ActivityHeatmap]', err)
  }
}

// KEY-IN-STATE tables (I18N-CONVENTION §1.3): a LITERAL bundle key per
// activity type, and a one / many pair for the day tooltip's counts — Georgian
// keeps a noun singular after a numeral, English does not. An unknown type
// passes through verbatim, which polyglot echoes.
const _typeLabelKey = {
  standup: 'tickets.heatmap.type.standup',
  ticket_update: 'tickets.heatmap.type.ticketUpdate',
  note_edit: 'tickets.heatmap.type.noteEdit',
  kb_edit: 'tickets.heatmap.type.kbEdit',
  meet_call: 'tickets.heatmap.type.meetCall'
}
const _typeCountKeys = {
  ticket_update: [
    'tickets.heatmap.count.ticketUpdateOne',
    'tickets.heatmap.count.ticketUpdateMany'
  ],
  note_edit: ['tickets.heatmap.count.noteEditOne', 'tickets.heatmap.count.noteEditMany'],
  kb_edit: ['tickets.heatmap.count.kbEditOne', 'tickets.heatmap.count.kbEditMany'],
  meet_call: ['tickets.heatmap.count.meetCallOne', 'tickets.heatmap.count.meetCallMany']
}
const _ticketsTypeLabel = (el, type) => el.call('polyglot', _typeLabelKey[type] || type || '')
const _counted = (el, pair, n) => {
  const sentence = el.call('polyglot', pair[n === 1 ? 0 : 1])
  return sentence.replace('{count}', String(n))
}

// Takes BOTH the element and the root state, deliberately: `.update()` is a
// STATE method and `.call()` is an ELEMENT method. Passing only `s.root` (as
// this did until 2026-08-10) made every `.call('getSDK')` here throw
// "root.call is not a function" — silently, into a catch — so the heatmap
// never loaded a single day of data.
const _loadDetails = async (el, root, date) => {
  // WS-SWITCH-INCOMPLETE-1: the rows below name workspace A's ticket and note
  // ids (`source_table` / `source_id` / `metadata`), and they are written after
  // an await. Captured before the RPC — see sameWorkspaceWriter's note.
  const _write = sameWorkspaceWriter(root)
  root.update({ _activitySelectedDate: date, _activityDetails: null })
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.activity listEvents
    const result = await el.call('getSDK').execute('workspaceProject.activity', 'listEvents', {
      filter: {
        user_email: root.currentUserEmail,
        occurred_on: date,
        select: 'activity_type, score, source_table, source_id, metadata, created_at'
      },
      order: { column: 'created_at', ascending: true }
    })
    const data = Array.isArray(result?.data) ? result.data : result?.data || result || []
    // ⛔ THE PATCH IS ONE KEY, AND THE DROP IS COMPLETE. Unlike globalSearch,
    // there is no transport flag to settle separately: the panel's `hide:`
    // reads `_activitySelectedDate`, which the switch already wiped to null,
    // so a dropped reply leaves the drill-down CLOSED rather than spinning.
    _write({ _activityDetails: data || [] })
  } catch (err) {
    console.error('[ActivityHeatmap details]', err)
    _write({ _activityDetails: [] })
  }
}

export const TicketsHeatmapCell = {
  width: '100%',
  aspectRatio: '1',
  // A placeholder cell (the grid before first paint) holds the row height only.
  visibility: (el, s) => (s.placeholder ? 'hidden' : 'visible'),
  round: 'X',
  background: (el, s) => _intensity(s.score, s.standups > 0),
  border: 'none',
  cursor: (el, s) => (s.score || s.standups ? 'pointer' : 'default'),
  transition: 'background Z defaultBezier',
  ':hover': { outline: '1px solid var(--color-title)' },
  title: (el, s) => {
    if (!s.score && !s.standups) return s.date
    const parts = []
    if (s.standups) parts.push(el.call('polyglot', 'tickets.heatmap.tooltip.standup'))
    const bd = s.breakdown || {}
    for (const [k, n] of Object.entries(bd)) {
      if (k === 'standup') continue
      // A type with no count pair (an unknown one) keeps "n type".
      parts.push(
        _typeCountKeys[k] ? _counted(el, _typeCountKeys[k], n) : `${n} ${_ticketsTypeLabel(el, k)}`
      )
    }
    const points = _counted(
      el,
      ['tickets.heatmap.count.pointOne', 'tickets.heatmap.count.pointMany'],
      s.score
    )
    return `${s.date} · ${points}${parts.length ? ' — ' + parts.join(', ') : ''}`
  },
  onClick: (e, el, s) => {
    if (!s.score && !s.standups) return
    _loadDetails(el, s.root, s.date)
  }
}

const _typeColor = (t) =>
  t === 'standup'
    ? 'green'
    : t === 'meet_call'
      ? 'purple'
      : t === 'kb_edit' || t === 'note_edit'
        ? 'cerulean'
        : 'blue'

const _hhmmss = (ts) => {
  try {
    return new Date(ts).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  } catch {
    return ''
  }
}

const _detailFor = (s) => {
  const md = s.metadata || {}
  return (
    md.title ||
    md.summary ||
    md.body ||
    (s.source_id ? `${s.source_table || 'item'}·${s.source_id}` : '')
  )
}

export const TicketsActivityRow = {
  flow: 'x',
  gap: 'Z',
  align: 'baseline left',
  padding: 'X Z',
  fontFamily: 'mono',
  fontSize: 'Y2',
  lineHeight: '1.5',
  ':hover': { background: 'hover' },

  Time: {
    tag: 'span',
    color: 'caption',
    flexShrink: '0',
    text: (el, s) => _hhmmss(s.created_at)
  },

  Type: {
    tag: 'span',
    flexShrink: '0',
    minWidth: 'C',
    fontWeight: '700',
    color: (el, s) => _typeColor(s.activity_type),
    // CSS uppercase — `String#toUpperCase` turns a Georgian label into Mtavruli.
    textTransform: 'uppercase',
    text: (el, s) => _ticketsTypeLabel(el, s.activity_type)
  },

  Detail: {
    tag: 'span',
    flex: '1',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => _detailFor(s)
  }
}
